import React, { useMemo } from 'react';
import { Box, SxProps, Typography, Tooltip, useTheme } from '@mui/material';
import TitleCase from 'components/formatting/TitleCase';
import ArcChart from 'components/charting/ArcChart';
import { Query } from 'system/query/Query';
import { DashboardQueryDrilldownDialog } from 'components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog';
import { CategoryWidgetData, MakeCategorySegmentsFromSingleResponse, MakeCategorySegmentsFromMultiResponses } from 'components/widgets/utility/CategoryChartsAPIResponseHandlers';
import { defaultSettings, getMaximumDisplayCountOptions } from './CategoryArcsSettings';
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import { useTranslation } from 'react-i18next';
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';
import { getSingleActiveWidgetRequestByName, getWidgetResponsesByName, widgetQueryTitleByResponses } from 'system/widget/Widget';

function CategoryArcsContent(props: WithQueryWidgetProps): JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const [drilldownTitle, setDrilldownTitle] = React.useState<string | null>(null);
  const [drilldownQuery, setDrilldownQuery] = React.useState<Query | null>(null);
  const [drilldownQueryIds, setDrilldownQueryIds] = React.useState<string[]>([]);

  const currentState = {
    ...defaultSettings,
    ...props.widget?.data,
    ...props.widget?.data?.settings
  };

  const categoryData:CategoryWidgetData | undefined = useMemo(() => {
    const apiResponses = getWidgetResponsesByName(props.apiResponses, "category-arcs");
    if (!apiResponses) {
      return undefined;
    }

    // If there are multiple responses, or if the widget is set to always show multi-template-queries,
    // then we treat this as a multi-query category bar chart
    if (Array.isArray(apiResponses) && apiResponses.length > 1) {
      return MakeCategorySegmentsFromMultiResponses(apiResponses, props.queryInfo, theme, i18n);
    } else {
      let apiResponse = apiResponses;
      if (Array.isArray(apiResponses)) {
        if (apiResponses.length === 0) {
          return undefined;
        }
        apiResponse = apiResponses[0];
      }
      // Single response
      // We assume that the response is for a single query, so we can directly pass
      return MakeCategorySegmentsFromSingleResponse(apiResponse, props.queryInfo, theme);
    }
  }, [props.apiResponses, props.queryInfo, props.widget, theme]);

  const { arcStyle, arcInnerStyle, containerStyle } = useMemo((): {
    arcStyle: SxProps;
    arcInnerStyle: SxProps;
    containerStyle: SxProps;
  } => {
    const arcWidths: Record<string, string> = { s: "100px", m: "200px", l: "350px" };

    let columns = currentState.columns || 3;
    if (categoryData?.categoryRows?.length && categoryData.categoryRows.length < columns) {
      columns = categoryData.categoryRows.length;
    }

    const basePt = currentState.widgetTitle === 'none' ? 2 : 0;

    // Auto horizontal layout
    if (currentState.layout === "h") {
      return {
        containerStyle: { overflowY: "auto", p: 2, pt: basePt, display: "flex", flexWrap: "wrap", justifyContent: "space-around" },
        arcStyle: { display: "inline-block", maxWidth: "100%", height: "100%", aspectRatio: "1 / 1 !important", mb: 2 },
        arcInnerStyle: { display: "grid", p: 0, gridTemplateRows: "auto 1fr", textAlign: "center" },
      };
    }

    // Auto vertical layout
    if (currentState.layout === "v") {
      return {
        containerStyle: { overflowY: "auto", p: 2, pt: basePt },
        arcStyle: { display: "block", maxWidth: "100%", maxHeight: "100%", aspectRatio: "1 / 1 !important", mb: 2 },
        arcInnerStyle: { display: "grid", p: 0, gridTemplateRows: "auto 1fr", textAlign: "center" },
      };
    }

    // Default to fixed 3 columns
    return {
      containerStyle: { overflowY: "auto", p: 2, pt: basePt, display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)` },
      arcStyle: { display: "block", maxHeight: "100%", mb: 2, textAlign: "center" },
      arcInnerStyle: { margin: "0 auto", width: arcWidths[currentState.arcSize || defaultSettings.arcSize] },
    };
  }, [currentState.layout, currentState.arcSize, currentState.widgetTitle, currentState.columns, categoryData?.categoryRows?.length]);

  const displayCountOptions = getMaximumDisplayCountOptions(i18n);
  const selectedDisplayCount = displayCountOptions.find((option) => option.value === currentState.maxDisplayed);
  const displayCount = Math.min(selectedDisplayCount?.value ?? defaultSettings.maxDisplayed, categoryData?.categoryRows?.length ?? 0);
  const isMultiQuery = Array.isArray(props.activeWidgetRequests[0]) && props.activeWidgetRequests[0].length > 1;

    const sortedRows = useMemo(() => {
      const rows = [...(categoryData?.categoryRows ?? [])];

      if (isMultiQuery) {
        switch (currentState.sortValue) {
          case "asc":
            rows.sort(
              (a, b) =>
                (a.percent ?? 0) - (b.percent ?? 0) ||
                (a.label ?? "").localeCompare(b.label ?? "")
            );
            break;
          case "desc":
            rows.sort(
              (a, b) =>
                (b.percent ?? 0) - (a.percent ?? 0) ||
                (a.label ?? "").localeCompare(b.label ?? "")
            );
            break;
          case "none":
          default:
            break;
        }
      }

      return rows.slice(0, displayCount);
    }, [categoryData?.categoryRows, currentState.sortValue, displayCount]);


  const displayRows = useMemo(() => {
    return sortedRows.map((row, index) => {
      const drilldown = () => {
        if (row.drilldownQuery) {
          setDrilldownQuery(row.drilldownQuery);
          setDrilldownTitle(row.label);
          const request = getSingleActiveWidgetRequestByName(props.activeWidgetRequests, "category-arcs");
          if (request) {
            setDrilldownQueryIds(request.activeQueryIds);
          }
        }
      };

      return (
        <Box key={`${row.key || row.label || "row"}-${index}`} sx={arcStyle}>
          <Box sx={{ ...arcInnerStyle, cursor: "pointer" }} onClick={drilldown}>
            <Tooltip title={<TitleCase text={row.label || ""} />}>
              <Typography
                variant="subtitle2"
                sx={{ textAlign: "center", width: "100%", overflow: "hidden", height: "1.5em", pr: 1 }}
                noWrap
              >
                <TitleCase text={row.label || ""} />
              </Typography>
            </Tooltip>
            <Box sx={{ position: "relative", height: "100%" }}>
              <ArcChart
                percentage={row.percent ?? 0}
                value={row.count ?? 0}
                fieldLabel={row.label}
                color={row.color}
              />
            </Box>
          </Box>
        </Box>
      );
    });
  }, [categoryData?.categoryRows, sortedRows, displayCount, arcStyle, arcInnerStyle, props.activeWidgetRequests]);

  const widgetQueryTitle = widgetQueryTitleByResponses(props.apiResponses, "category-arcs", i18n);

  return (
    <>
      {displayRows.length !== 0 &&
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={currentState.widgetTitleAlignment}
              customTitle={currentState.customWidgetTitle}
              defaultTitle={(categoryData?.fieldLabel ?? "")}
              settingsTitle={currentState.widgetTitle}
              widgetQueryTitle={widgetQueryTitle} />
          }
        >
          {displayRows &&
            <Box
              sx={[{
                height: "100%"
              }, ...(Array.isArray(containerStyle) ? containerStyle : [containerStyle])]}>
              {displayRows}
            </Box>
          }
          {drilldownQuery && drilldownTitle && drilldownQueryIds &&
            <DashboardQueryDrilldownDialog
              title={drilldownTitle}
              queryInfo={props.queryInfo}
              onClose={() => setDrilldownQuery(null)}
              activeQueryIds={drilldownQueryIds}
              drilldownQuery={drilldownQuery}
              widgetRequestInfo={props.widget?.request_info}
              />
          }
        </WidgetLayout>
      }
      {displayRows.length === 0 &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
    </>
  );
}

export default withQueryWidget(CategoryArcsContent, {
  hasSettingsDialog: true,
  hasQueryDialog: true,
});